import type { SkeletonConfigT, SkeletonTemplateT } from '../../../types/annotations.type';
export const SKELETON_TEMPLATE_BOUNDING_BOX_PADDING = 30;
export const SKELETON_PREVIEW_STYLE = ({
  BOX_FILL_OPACITY: 0.1,
  EDGE_OPACITY: 0.7,
  NODE_RADIUS: 4,
  NODE_OPACITY: 0.8,
  NODE_STROKE_COLOR: 'white',
  NODE_STROKE_WIDTH: 1,
  DEFAULT_COLOR: '#0066FF'
} as const);
export const HUMAN_SKELETON_TEMPLATE: SkeletonConfigT = (({
  canvas: {
    height: 875,
    width: 750
  },
  skeleton: {
    nodes: [{
      id: 'nose',
      point: [375, 170.83]
    }, {
      id: 'left_eye',
      point: [350, 155.83]
    }, {
      id: 'right_eye',
      point: [400, 155.83]
    }, {
      id: 'left_ear',
      point: [325, 170.83]
    }, {
      id: 'right_ear',
      point: [425, 170.83]
    }, {
      id: 'left_shoulder',
      point: [300, 270.83]
    }, {
      id: 'right_shoulder',
      point: [450, 270.83]
    }, {
      id: 'left_elbow',
      point: [250, 370.83]
    }, {
      id: 'right_elbow',
      point: [500, 370.83]
    }, {
      id: 'left_wrist',
      point: [225, 445.83]
    }, {
      id: 'right_wrist',
      point: [525, 445.83]
    }, {
      id: 'left_hip',
      point: [325, 520.83]
    }, {
      id: 'right_hip',
      point: [425, 520.83]
    }, {
      id: 'left_knee',
      point: [315, 645.83]
    }, {
      id: 'right_knee',
      point: [435, 645.83]
    }, {
      id: 'left_ankle',
      point: [305, 770.83]
    }, {
      id: 'right_ankle',
      point: [445, 770.83]
    }, {
      id: 'left_heel',
      point: [300, 805]
    }, {
      id: 'right_heel',
      point: [450, 805]
    }, {
      id: 'left_big_toe',
      point: [275, 862.5]
    }, {
      id: 'right_big_toe',
      point: [475, 862.5]
    }, {
      id: 'left_little_toe',
      point: [300, 862.5]
    }, {
      id: 'right_little_toe',
      point: [450, 862.5]
    }],
    edges: [{
      startNode: 'right_ankle',
      endNode: 'right_knee'
    }, {
      startNode: 'right_knee',
      endNode: 'right_hip'
    }, {
      startNode: 'left_ankle',
      endNode: 'left_knee'
    }, {
      startNode: 'left_knee',
      endNode: 'left_hip'
    }, {
      startNode: 'right_hip',
      endNode: 'left_hip'
    }, {
      startNode: 'right_shoulder',
      endNode: 'right_hip'
    }, {
      startNode: 'left_shoulder',
      endNode: 'left_hip'
    }, {
      startNode: 'right_shoulder',
      endNode: 'left_shoulder'
    }, {
      startNode: 'right_shoulder',
      endNode: 'right_elbow'
    }, {
      startNode: 'right_elbow',
      endNode: 'right_wrist'
    }, {
      startNode: 'left_shoulder',
      endNode: 'left_elbow'
    }, {
      startNode: 'left_elbow',
      endNode: 'left_wrist'
    }, {
      startNode: 'left_ear',
      endNode: 'left_eye'
    }, {
      startNode: 'left_eye',
      endNode: 'nose'
    }, {
      startNode: 'nose',
      endNode: 'right_eye'
    }, {
      startNode: 'right_eye',
      endNode: 'right_ear'
    }, {
      startNode: 'right_ankle',
      endNode: 'right_heel'
    }, {
      startNode: 'right_heel',
      endNode: 'right_big_toe'
    }, {
      startNode: 'right_heel',
      endNode: 'right_little_toe'
    }, {
      startNode: 'left_ankle',
      endNode: 'left_heel'
    }, {
      startNode: 'left_heel',
      endNode: 'left_big_toe'
    }, {
      startNode: 'left_heel',
      endNode: 'left_little_toe'
    }]
  }
} as const) satisfies SkeletonConfigT);

// Node coords remapped to 0–1 range based on actual skeleton bounds:
// X: min=225, max=525 (range=300), Y: min=155.83, max=862.5 (range=706.67)
const X_MIN = 225;
const X_RANGE = 300;
const Y_MIN = 155.83;
const Y_RANGE = 706.67;
const nx = (px: number) => (px - X_MIN) / X_RANGE;
const ny = (py: number) => (py - Y_MIN) / Y_RANGE;
export const HUMAN_SKELETON_TEMPLATE_NORMALIZED: SkeletonTemplateT = {
  id: 'human-skeleton-default',
  name: 'Human Skeleton',
  aspectRatio: X_RANGE / Y_RANGE,
  nodes: [{
    id: 'nose',
    label: 'Nose',
    x: nx(375),
    y: ny(170.83)
  }, {
    id: 'left_eye',
    label: 'Left Eye',
    x: nx(350),
    y: ny(155.83)
  }, {
    id: 'right_eye',
    label: 'Right Eye',
    x: nx(400),
    y: ny(155.83)
  }, {
    id: 'left_ear',
    label: 'Left Ear',
    x: nx(325),
    y: ny(170.83)
  }, {
    id: 'right_ear',
    label: 'Right Ear',
    x: nx(425),
    y: ny(170.83)
  }, {
    id: 'left_shoulder',
    label: 'Left Shoulder',
    x: nx(300),
    y: ny(270.83)
  }, {
    id: 'right_shoulder',
    label: 'Right Shoulder',
    x: nx(450),
    y: ny(270.83)
  }, {
    id: 'left_elbow',
    label: 'Left Elbow',
    x: nx(250),
    y: ny(370.83)
  }, {
    id: 'right_elbow',
    label: 'Right Elbow',
    x: nx(500),
    y: ny(370.83)
  }, {
    id: 'left_wrist',
    label: 'Left Wrist',
    x: nx(225),
    y: ny(445.83)
  }, {
    id: 'right_wrist',
    label: 'Right Wrist',
    x: nx(525),
    y: ny(445.83)
  }, {
    id: 'left_hip',
    label: 'Left Hip',
    x: nx(325),
    y: ny(520.83)
  }, {
    id: 'right_hip',
    label: 'Right Hip',
    x: nx(425),
    y: ny(520.83)
  }, {
    id: 'left_knee',
    label: 'Left Knee',
    x: nx(315),
    y: ny(645.83)
  }, {
    id: 'right_knee',
    label: 'Right Knee',
    x: nx(435),
    y: ny(645.83)
  }, {
    id: 'left_ankle',
    label: 'Left Ankle',
    x: nx(305),
    y: ny(770.83)
  }, {
    id: 'right_ankle',
    label: 'Right Ankle',
    x: nx(445),
    y: ny(770.83)
  }, {
    id: 'left_heel',
    label: 'Left Heel',
    x: nx(300),
    y: ny(805)
  }, {
    id: 'right_heel',
    label: 'Right Heel',
    x: nx(450),
    y: ny(805)
  }, {
    id: 'left_big_toe',
    label: 'Left Big Toe',
    x: nx(275),
    y: ny(862.5)
  }, {
    id: 'right_big_toe',
    label: 'Right Big Toe',
    x: nx(475),
    y: ny(862.5)
  }, {
    id: 'left_little_toe',
    label: 'Left Little Toe',
    x: nx(300),
    y: ny(862.5)
  }, {
    id: 'right_little_toe',
    label: 'Right Little Toe',
    x: nx(450),
    y: ny(862.5)
  }],
  edges: [{
    sourceNodeId: 'right_ankle',
    targetNodeId: 'right_knee'
  }, {
    sourceNodeId: 'right_knee',
    targetNodeId: 'right_hip'
  }, {
    sourceNodeId: 'left_ankle',
    targetNodeId: 'left_knee'
  }, {
    sourceNodeId: 'left_knee',
    targetNodeId: 'left_hip'
  }, {
    sourceNodeId: 'right_hip',
    targetNodeId: 'left_hip'
  }, {
    sourceNodeId: 'right_shoulder',
    targetNodeId: 'right_hip'
  }, {
    sourceNodeId: 'left_shoulder',
    targetNodeId: 'left_hip'
  }, {
    sourceNodeId: 'right_shoulder',
    targetNodeId: 'left_shoulder'
  }, {
    sourceNodeId: 'right_shoulder',
    targetNodeId: 'right_elbow'
  }, {
    sourceNodeId: 'right_elbow',
    targetNodeId: 'right_wrist'
  }, {
    sourceNodeId: 'left_shoulder',
    targetNodeId: 'left_elbow'
  }, {
    sourceNodeId: 'left_elbow',
    targetNodeId: 'left_wrist'
  }, {
    sourceNodeId: 'left_ear',
    targetNodeId: 'left_eye'
  }, {
    sourceNodeId: 'left_eye',
    targetNodeId: 'nose'
  }, {
    sourceNodeId: 'nose',
    targetNodeId: 'right_eye'
  }, {
    sourceNodeId: 'right_eye',
    targetNodeId: 'right_ear'
  }, {
    sourceNodeId: 'right_ankle',
    targetNodeId: 'right_heel'
  }, {
    sourceNodeId: 'right_heel',
    targetNodeId: 'right_big_toe'
  }, {
    sourceNodeId: 'right_heel',
    targetNodeId: 'right_little_toe'
  }, {
    sourceNodeId: 'left_ankle',
    targetNodeId: 'left_heel'
  }, {
    sourceNodeId: 'left_heel',
    targetNodeId: 'left_big_toe'
  }, {
    sourceNodeId: 'left_heel',
    targetNodeId: 'left_little_toe'
  }]
};
export const HUMAN_SKELETON_TEMPLATE_COLORED: SkeletonTemplateT = {
  id: 'human-skeleton-colored',
  name: 'Human Skeleton (Colored)',
  description: 'Body-region colored skeleton for visualization',
  aspectRatio: X_RANGE / Y_RANGE,
  nodes: [{
    id: 'nose',
    label: 'Nose',
    x: nx(375),
    y: ny(170.83),
    color: '#FF6B6B'
  }, {
    id: 'left_eye',
    label: 'Left Eye',
    x: nx(350),
    y: ny(155.83),
    color: '#FF6B6B'
  }, {
    id: 'right_eye',
    label: 'Right Eye',
    x: nx(400),
    y: ny(155.83),
    color: '#FF6B6B'
  }, {
    id: 'left_ear',
    label: 'Left Ear',
    x: nx(325),
    y: ny(170.83),
    color: '#FF6B6B'
  }, {
    id: 'right_ear',
    label: 'Right Ear',
    x: nx(425),
    y: ny(170.83),
    color: '#FF6B6B'
  }, {
    id: 'left_shoulder',
    label: 'Left Shoulder',
    x: nx(300),
    y: ny(270.83),
    color: '#4ECDC4'
  }, {
    id: 'right_shoulder',
    label: 'Right Shoulder',
    x: nx(450),
    y: ny(270.83),
    color: '#4ECDC4'
  }, {
    id: 'left_elbow',
    label: 'Left Elbow',
    x: nx(250),
    y: ny(370.83),
    color: '#45B7D1'
  }, {
    id: 'right_elbow',
    label: 'Right Elbow',
    x: nx(500),
    y: ny(370.83),
    color: '#45B7D1'
  }, {
    id: 'left_wrist',
    label: 'Left Wrist',
    x: nx(225),
    y: ny(445.83),
    color: '#96CEB4'
  }, {
    id: 'right_wrist',
    label: 'Right Wrist',
    x: nx(525),
    y: ny(445.83),
    color: '#96CEB4'
  }, {
    id: 'left_hip',
    label: 'Left Hip',
    x: nx(325),
    y: ny(520.83),
    color: '#FFEAA7'
  }, {
    id: 'right_hip',
    label: 'Right Hip',
    x: nx(425),
    y: ny(520.83),
    color: '#FFEAA7'
  }, {
    id: 'left_knee',
    label: 'Left Knee',
    x: nx(315),
    y: ny(645.83),
    color: '#DDA0DD'
  }, {
    id: 'right_knee',
    label: 'Right Knee',
    x: nx(435),
    y: ny(645.83),
    color: '#DDA0DD'
  }, {
    id: 'left_ankle',
    label: 'Left Ankle',
    x: nx(305),
    y: ny(770.83),
    color: '#FF8C42'
  }, {
    id: 'right_ankle',
    label: 'Right Ankle',
    x: nx(445),
    y: ny(770.83),
    color: '#FF8C42'
  }, {
    id: 'left_heel',
    label: 'Left Heel',
    x: nx(300),
    y: ny(805),
    color: '#FF8C42'
  }, {
    id: 'right_heel',
    label: 'Right Heel',
    x: nx(450),
    y: ny(805),
    color: '#FF8C42'
  }, {
    id: 'left_big_toe',
    label: 'Left Big Toe',
    x: nx(275),
    y: ny(862.5),
    color: '#FF8C42'
  }, {
    id: 'right_big_toe',
    label: 'Right Big Toe',
    x: nx(475),
    y: ny(862.5),
    color: '#FF8C42'
  }, {
    id: 'left_little_toe',
    label: 'Left Little Toe',
    x: nx(300),
    y: ny(862.5),
    color: '#FF8C42'
  }, {
    id: 'right_little_toe',
    label: 'Right Little Toe',
    x: nx(450),
    y: ny(862.5),
    color: '#FF8C42'
  }],
  edges: HUMAN_SKELETON_TEMPLATE_NORMALIZED.edges
};